import { memo, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useGetTopProductsQuery } from '../../slices/productApiSlice';
import { useSelector } from 'react-redux';
import { taxMultiplier } from '../../utils/priceUtils';

import '../../assets/styles/kiosk.idle.carousel.css';

const DEFAULT_IMAGE = '/images/default.jpg';
const CAROUSEL_INTERVAL_MS = 4500;

const KioskIdleCarousel = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const [activeIndex, setActiveIndex] = useState(0);
    const { data: products, isLoading, isError } = useGetTopProductsQuery(
        { priceAssigned: userInfo?.priceAssigned || null },
        { refetchOnFocus: false, refetchOnReconnect: false }
    );

    const slides = useMemo(
        () =>
            (products || []).map((product) => {
                const basePrice = Number(product.price || 0);
                const priceWithTax = basePrice * taxMultiplier(product.taxPercentage ?? 0);
                return {
                    id: product._id,
                    name: product.name || 'Producto destacado',
                    imageSrc: product.image || DEFAULT_IMAGE,
                    priceText: priceWithTax.toFixed(2),
                };
            }),
        [products]
    );

    useEffect(() => {
        setActiveIndex(0);
    }, [slides.length]);

    useEffect(() => {
        if (slides.length <= 1) return undefined;

        const timer = setInterval(() => {
            setActiveIndex((previous) => (previous + 1) % slides.length);
        }, CAROUSEL_INTERVAL_MS);

        return () => clearInterval(timer);
    }, [slides.length]);

    if (isLoading || isError || !slides.length) return null;

    const activeProduct = slides[activeIndex] || slides[0];

    return (
        <div className="kiosk-idle-carousel">
            <div className="kiosk-idle-slide">
                <div className="kiosk-idle-content">
                    <div className="kiosk-idle-result-card">
                        <Row className="g-0 align-items-stretch">
                            <Col
                                xs={12}
                                md={5}
                                lg={4}
                                className="d-flex align-items-center justify-content-center kiosk-idle-result-image-col"
                            >
                                <div className="kiosk-idle-image-wrap">
                                    <img
                                        src={activeProduct.imageSrc}
                                        alt={activeProduct.name}
                                        className="kiosk-idle-product-img"
                                        decoding="async"
                                        onError={(event) => {
                                            event.currentTarget.src = DEFAULT_IMAGE;
                                        }}
                                    />
                                </div>
                            </Col>

                            <Col
                                xs={12}
                                md={7}
                                lg={8}
                                className="d-flex flex-column justify-content-center align-items-center kiosk-idle-result-info-col text-center"
                            >
                                <div className="kiosk-idle-info">
                                    <h2 className="kiosk-idle-name">{activeProduct.name}</h2>

                                    <div className="kiosk-idle-price-block">
                                        <div className="kiosk-idle-price-label">PRECIO</div>
                                        <p className="kiosk-idle-price mb-0">
                                            <span className="kiosk-idle-price-sym">$</span>
                                            {activeProduct.priceText}
                                        </p>
                                    </div>

                                    <p className="kiosk-idle-hint mt-3 mb-0">
                                        Escanea para verificar un precio
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {slides.length > 1 && (
                        <div className="kiosk-idle-indicators" aria-hidden="true">
                            {slides.map((slide, index) => (
                                <span
                                    key={slide.id}
                                    className={`kiosk-idle-dot ${index === activeIndex ? 'is-active' : ''}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="kiosk-idle-cta">
                <span className="kiosk-idle-cta-scan" aria-hidden="true">⬛</span>
                <span>Escanea o ingresa el código de barras del producto</span>
            </div>
        </div>
    );
};

export default memo(KioskIdleCarousel);
